import { useEffect, useRef, useCallback } from 'react'

/* ─────────────────────────────────────────────────────────────────────────────
 * LivingIdentityBackground — "Guess Who?" Identity Board
 *
 * A grid of floating identity icons, mostly hidden in darkness, revealed by a
 * circular spotlight that follows the mouse cursor. No card frames — just the
 * bare icon outlines emerging from the void.
 *
 * Color palette: varied — white, indigo, teal, cyan, purple — NOT monotone.
 * Each icon gets a random color from the palette for visual richness.
 *
 * Features:
 *  - Spotlight mouse interaction (220px radius, warm white glow)
 *  - 12 procedurally drawn identity icons in varied colors
 *  - Scroll-responsive states
 *  - Click triggers full-board flash
 *  - Shield sonar pulses
 *  - Mobile: ambient breathing, tap-to-pulse
 * ───────────────────────────────────────────────────────────────────────────── */

interface IdentityIcon {
  x: number
  y: number
  icon: number
  color: number          // index into COLOR_PALETTE
  revealAmount: number
  targetReveal: number
  phase: number
  size: number           // slight variation per icon
}

interface ScanParticle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  opacity: number
  color: number
}

interface ShieldPulse {
  x: number
  y: number
  radius: number
  maxRadius: number
  opacity: number
}

type ScrollState = 'sovereign' | 'learning' | 'expanding' | 'fortifying' | 'inviting'

interface Props {
  opacity?: number
  absolute?: boolean
  zIndex?: number
  className?: string
}

// ── Constants ──────────────────────────────────────────────────────────
const ICON_SPACING = 90   // distance between icon centers
const SPOTLIGHT_RADIUS = 220
const SPOTLIGHT_AMBIENT = 380
const NUM_ICONS = 12

// Varied color palette — NOT all teal
const COLOR_PALETTE = [
  { r: 255, g: 255, b: 255 },  // white
  { r: 165, g: 180, b: 252 },  // indigo-300
  { r: 129, g: 140, b: 248 },  // indigo-400
  { r: 45, g: 212, b: 191 },   // teal-400
  { r: 34, g: 211, b: 238 },   // cyan-400
  { r: 192, g: 132, b: 252 },  // purple-400
  { r: 147, g: 197, b: 253 },  // blue-300
  { r: 196, g: 181, b: 253 },  // violet-300
]

function getScrollState(scrollY: number): ScrollState {
  if (scrollY < 300) return 'sovereign'
  if (scrollY < 900) return 'learning'
  if (scrollY < 1500) return 'expanding'
  if (scrollY < 2200) return 'fortifying'
  return 'inviting'
}

// ── Icon Drawing Functions ─────────────────────────────────────────────
type IconDrawer = (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, r: number, g: number, b: number, alpha: number) => void

const drawFingerprint: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1
  for (let i = 1; i <= 4; i++) {
    const rad = size * (i / 5)
    ctx.beginPath()
    ctx.arc(cx, cy + size * 0.1, rad, -Math.PI * 0.8, Math.PI * 0.1 + i * 0.15)
    ctx.stroke()
  }
}

const drawShield: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.moveTo(cx, cy - size)
  ctx.lineTo(cx + size * 0.85, cy - size * 0.5)
  ctx.lineTo(cx + size * 0.7, cy + size * 0.4)
  ctx.lineTo(cx, cy + size)
  ctx.lineTo(cx - size * 0.7, cy + size * 0.4)
  ctx.lineTo(cx - size * 0.85, cy - size * 0.5)
  ctx.closePath()
  ctx.stroke()
}

const drawLock: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  const bw = size * 1.2, bh = size * 0.9
  ctx.strokeRect(cx - bw / 2, cy - bh / 4, bw, bh)
  ctx.beginPath()
  ctx.arc(cx, cy - bh / 4, size * 0.45, Math.PI, 0)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cy + bh * 0.15, size * 0.12, 0, Math.PI * 2)
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.fill()
}

const drawKey: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.arc(cx - size * 0.3, cy - size * 0.3, size * 0.4, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(cx, cy)
  ctx.lineTo(cx + size * 0.7, cy + size * 0.7)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(cx + size * 0.4, cy + size * 0.4)
  ctx.lineTo(cx + size * 0.6, cy + size * 0.2)
  ctx.moveTo(cx + size * 0.55, cy + size * 0.55)
  ctx.lineTo(cx + size * 0.75, cy + size * 0.35)
  ctx.stroke()
}

const drawEye: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.moveTo(cx - size, cy)
  ctx.quadraticCurveTo(cx, cy - size * 0.7, cx + size, cy)
  ctx.quadraticCurveTo(cx, cy + size * 0.7, cx - size, cy)
  ctx.closePath()
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.32, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.12, 0, Math.PI * 2)
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.fill()
}

const drawFace: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.ellipse(cx, cy - size * 0.15, size * 0.6, size * 0.8, 0, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(cx - size * 0.2, cy - size * 0.3, size * 0.06, 0, Math.PI * 2)
  ctx.arc(cx + size * 0.2, cy - size * 0.3, size * 0.06, 0, Math.PI * 2)
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.7})`
  ctx.fill()
}

const drawDNA: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1
  const steps = 8
  for (let strand = 0; strand < 2; strand++) {
    ctx.beginPath()
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const y = cy - size + t * size * 2
      const x = cx + Math.sin(t * Math.PI * 2 + strand * Math.PI) * size * 0.5
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.stroke()
  }
  for (let i = 1; i < steps; i += 2) {
    const t = i / steps
    const y = cy - size + t * size * 2
    const x1 = cx + Math.sin(t * Math.PI * 2) * size * 0.5
    const x2 = cx + Math.sin(t * Math.PI * 2 + Math.PI) * size * 0.5
    ctx.beginPath()
    ctx.moveTo(x1, y)
    ctx.lineTo(x2, y)
    ctx.stroke()
  }
}

const drawHash: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.font = `${size * 1.8}px monospace`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.fillText('#', cx, cy)
}

const drawHexagon: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6
    const hx = cx + Math.cos(angle) * size
    const hy = cy + Math.sin(angle) * size
    if (i === 0) ctx.moveTo(hx, hy)
    else ctx.lineTo(hx, hy)
  }
  ctx.closePath()
  ctx.stroke()
}

const drawSignal: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1
  for (let i = 1; i <= 3; i++) {
    const rad = size * (i / 3)
    ctx.beginPath()
    ctx.arc(cx, cy + size * 0.3, rad, -Math.PI * 0.75, -Math.PI * 0.25)
    ctx.stroke()
  }
  ctx.beginPath()
  ctx.arc(cx, cy + size * 0.3, size * 0.1, 0, Math.PI * 2)
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.fill()
}

const drawStar: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1.2
  ctx.beginPath()
  for (let i = 0; i < 5; i++) {
    const outerAngle = (Math.PI * 2 * i) / 5 - Math.PI / 2
    const innerAngle = outerAngle + Math.PI / 5
    const ox = cx + Math.cos(outerAngle) * size
    const oy = cy + Math.sin(outerAngle) * size
    const ix = cx + Math.cos(innerAngle) * size * 0.4
    const iy = cy + Math.sin(innerAngle) * size * 0.4
    if (i === 0) ctx.moveTo(ox, oy)
    else ctx.lineTo(ox, oy)
    ctx.lineTo(ix, iy)
  }
  ctx.closePath()
  ctx.stroke()
}

const drawCircuit: IconDrawer = (ctx, cx, cy, size, r, g, b, alpha) => {
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.arc(cx, cy, size * 0.2, 0, Math.PI * 2)
  ctx.fill()
  const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]]
  for (const [dx, dy] of dirs) {
    ctx.beginPath()
    ctx.moveTo(cx + dx * size * 0.2, cy + dy * size * 0.2)
    ctx.lineTo(cx + dx * size * 0.8, cy + dy * size * 0.8)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(cx + dx * size * 0.8, cy + dy * size * 0.8, size * 0.08, 0, Math.PI * 2)
    ctx.fill()
  }
}

const ICON_DRAWERS: IconDrawer[] = [
  drawFingerprint, drawShield, drawLock, drawKey, drawEye, drawFace,
  drawDNA, drawHash, drawHexagon, drawSignal, drawStar, drawCircuit,
]

// ── Component ─────────────────────────────────────────────────────────
export default function LivingIdentityBackground({
  opacity = 0.60,
  absolute = false,
  zIndex = -5,
  className = '',
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mouseRef = useRef({ x: -9999, y: -9999 })
  const scrollRef = useRef(0)
  const flashRef = useRef(0)

  const handleClick = useCallback((e: MouseEvent) => {
    flashRef.current = 1.0
    mouseRef.current = { x: e.clientX, y: e.clientY }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let width = window.innerWidth
    let height = window.innerHeight
    const isMobile = width < 768

    const resizeCanvas = () => {
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width
      canvas.height = height
      buildGrid()
    }

    canvas.width = width
    canvas.height = height

    const handleMouseMove = (e: MouseEvent) => { mouseRef.current = { x: e.clientX, y: e.clientY } }
    const handleScroll = () => { scrollRef.current = window.scrollY }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', resizeCanvas)
    canvas.addEventListener('click', handleClick)

    // ── State ───────────────────────────────────────────────────────
    let icons: IdentityIcon[] = []
    let particles: ScanParticle[] = []
    let shieldPulses: ShieldPulse[] = []
    let lastPulseTime = 0

    function buildGrid() {
      icons = []
      particles = []

      const spacing = isMobile ? ICON_SPACING * 1.3 : ICON_SPACING
      const cols = Math.ceil(width / spacing) + 2
      const rows = Math.ceil(height / spacing) + 2
      const totalW = cols * spacing
      const totalH = rows * spacing
      const offX = (width - totalW) / 2 + spacing / 2
      const offY = (height - totalH) / 2 + spacing / 2

      const target = isMobile ? 40 : 100
      const total = cols * rows
      const skip = Math.max(1, Math.floor(total / target))

      let idx = 0
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if (idx++ % skip !== 0) continue
          // Slight random offset so it doesn't look like a rigid grid
          const jitterX = (Math.random() - 0.5) * spacing * 0.25
          const jitterY = (Math.random() - 0.5) * spacing * 0.25
          icons.push({
            x: offX + col * spacing + jitterX,
            y: offY + row * spacing + jitterY,
            icon: Math.floor(Math.random() * NUM_ICONS),
            color: Math.floor(Math.random() * COLOR_PALETTE.length),
            revealAmount: 0,
            targetReveal: 0,
            phase: Math.random() * Math.PI * 2,
            size: 12 + Math.random() * 6,  // 12-18px icon radius
          })
        }
      }
    }

    buildGrid()

    // ── Animation Loop ──────────────────────────────────────────────
    let animFrame: number
    const startTime = Date.now()

    const animate = () => {
      const now = Date.now()
      const elapsed = (now - startTime) / 1000
      const mouse = mouseRef.current
      const scrollY = scrollRef.current
      const state = getScrollState(scrollY)

      ctx.clearRect(0, 0, width, height)
      ctx.fillStyle = 'rgb(5, 5, 18)'
      ctx.fillRect(0, 0, width, height)

      // ─ Deep nebula gradient ─
      const nebula = ctx.createRadialGradient(
        width * 0.5, height * 0.35, 0,
        width * 0.5, height * 0.5, Math.max(width, height) * 0.7
      )
      nebula.addColorStop(0, 'rgba(67, 56, 202, 0.07)')
      nebula.addColorStop(0.3, 'rgba(88, 28, 135, 0.04)')  // purple tint
      nebula.addColorStop(0.6, 'rgba(55, 48, 163, 0.02)')
      nebula.addColorStop(1, 'rgba(5, 5, 18, 0)')
      ctx.fillStyle = nebula
      ctx.fillRect(0, 0, width, height)

      // ─ State parameters ─
      let gridOpacity = 1.0
      let breathAmplitude = 0.05
      let particleSpeed = 1.0
      let pulseInterval = 5000

      switch (state) {
        case 'sovereign':   gridOpacity = 1.0;  breathAmplitude = 0.05; particleSpeed = 1.0; pulseInterval = 5000; break
        case 'learning':    gridOpacity = 0.85; breathAmplitude = 0.15; particleSpeed = 1.5; pulseInterval = 4000; break
        case 'expanding':   gridOpacity = 0.9;  breathAmplitude = 0.08; particleSpeed = 1.2; pulseInterval = 3500; break
        case 'fortifying':  gridOpacity = 1.0;  breathAmplitude = 0.12; particleSpeed = 0.8; pulseInterval = 2000; break
        case 'inviting':    gridOpacity = 0.4;  breathAmplitude = 0.06; particleSpeed = 0.5; pulseInterval = 8000; break
      }

      // ─ Spotlight glow (warm white, not teal) ─
      if (!isMobile && mouse.x > -1000) {
        const ambientGlow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, SPOTLIGHT_AMBIENT)
        ambientGlow.addColorStop(0, 'rgba(200, 200, 255, 0.04)')
        ambientGlow.addColorStop(0.3, 'rgba(165, 180, 252, 0.02)')
        ambientGlow.addColorStop(1, 'transparent')
        ctx.fillStyle = ambientGlow
        ctx.fillRect(0, 0, width, height)

        const spotGlow = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, SPOTLIGHT_RADIUS)
        spotGlow.addColorStop(0, 'rgba(220, 220, 255, 0.08)')
        spotGlow.addColorStop(0.4, 'rgba(165, 180, 252, 0.03)')
        spotGlow.addColorStop(1, 'transparent')
        ctx.fillStyle = spotGlow
        ctx.fillRect(0, 0, width, height)
      }

      // ─ Click flash (decays) ─
      const flash = flashRef.current
      if (flash > 0.01) { flashRef.current *= 0.92 } else { flashRef.current = 0 }

      // ─ Draw icons (NO card frames — just floating outlines) ─
      for (const ic of icons) {
        // Spotlight influence
        let spotInfluence = 0
        if (!isMobile && mouse.x > -1000) {
          const dx = ic.x - mouse.x
          const dy = ic.y - mouse.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          spotInfluence = Math.max(0, 1 - dist / SPOTLIGHT_RADIUS)
          spotInfluence *= spotInfluence  // ease-in falloff
        }

        const breathPulse = isMobile
          ? (Math.sin(elapsed * 0.8 + ic.phase) * 0.5 + 0.5) * breathAmplitude
          : 0

        const flashInfluence = flash * 0.6

        ic.targetReveal = Math.min(1, Math.max(spotInfluence, breathPulse, flashInfluence))

        const lerpSpeed = ic.targetReveal > ic.revealAmount ? 0.12 : 0.035
        ic.revealAmount += (ic.targetReveal - ic.revealAmount) * lerpSpeed

        const reveal = ic.revealAmount * gridOpacity
        if (reveal < 0.01) continue

        const c = COLOR_PALETTE[ic.color]
        const iconAlpha = Math.min(0.75, reveal * 0.9)

        // Soft glow behind icon when strongly revealed
        if (reveal > 0.25) {
          const glowR = ic.size * 3
          const glow = ctx.createRadialGradient(ic.x, ic.y, 0, ic.x, ic.y, glowR)
          glow.addColorStop(0, `rgba(${c.r}, ${c.g}, ${c.b}, ${reveal * 0.06})`)
          glow.addColorStop(0.5, `rgba(${c.r}, ${c.g}, ${c.b}, ${reveal * 0.015})`)
          glow.addColorStop(1, 'transparent')
          ctx.fillStyle = glow
          ctx.beginPath()
          ctx.arc(ic.x, ic.y, glowR, 0, Math.PI * 2)
          ctx.fill()
        }

        // Draw the icon outline (the main visual — no card/border/frame)
        ctx.save()
        ICON_DRAWERS[ic.icon](ctx, ic.x, ic.y, ic.size, c.r, c.g, c.b, iconAlpha)
        ctx.restore()
      }

      // ─ Scanning particles (varied colors) ─
      if (Math.random() < 0.03 * particleSpeed) {
        const edge = Math.floor(Math.random() * 4)
        const speed = (0.3 + Math.random() * 0.5) * particleSpeed
        let px: number, py: number, pvx: number, pvy: number
        switch (edge) {
          case 0: px = Math.random() * width; py = -10; pvx = (Math.random() - 0.5) * speed; pvy = speed; break
          case 1: px = width + 10; py = Math.random() * height; pvx = -speed; pvy = (Math.random() - 0.5) * speed; break
          case 2: px = Math.random() * width; py = height + 10; pvx = (Math.random() - 0.5) * speed; pvy = -speed; break
          default: px = -10; py = Math.random() * height; pvx = speed; pvy = (Math.random() - 0.5) * speed; break
        }
        particles.push({
          x: px, y: py, vx: pvx, vy: pvy,
          life: 0, maxLife: 300 + Math.random() * 200,
          opacity: 0.2 + Math.random() * 0.3,
          color: Math.floor(Math.random() * COLOR_PALETTE.length),
        })
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.x += p.vx
        p.y += p.vy
        p.life++

        if (p.life > p.maxLife || p.x < -50 || p.x > width + 50 || p.y < -50 || p.y > height + 50) {
          particles.splice(i, 1)
          continue
        }

        const lifeRatio = p.life / p.maxLife
        const fadeAlpha = lifeRatio < 0.1 ? lifeRatio / 0.1 : lifeRatio > 0.8 ? (1 - lifeRatio) / 0.2 : 1
        const alpha = p.opacity * fadeAlpha * gridOpacity
        const c = COLOR_PALETTE[p.color]

        const pg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 4)
        pg.addColorStop(0, `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha * 0.8})`)
        pg.addColorStop(0.5, `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha * 0.2})`)
        pg.addColorStop(1, 'transparent')
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
        ctx.fillStyle = pg
        ctx.fill()

        ctx.beginPath()
        ctx.arc(p.x, p.y, 0.8, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.6})`
        ctx.fill()
      }

      // ─ Shield pulses (indigo/white, not teal) ─
      if (now - lastPulseTime > pulseInterval + Math.random() * 2000) {
        let px: number, py: number
        if (!isMobile && mouse.x > -1000 && Math.random() < 0.4) {
          px = mouse.x; py = mouse.y
        } else if (icons.length > 0) {
          const ic = icons[Math.floor(Math.random() * icons.length)]
          px = ic.x; py = ic.y
        } else {
          px = width / 2; py = height / 2
        }
        shieldPulses.push({ x: px, y: py, radius: 0, maxRadius: 180 + Math.random() * 80, opacity: 0.25 })
        lastPulseTime = now
      }

      for (let i = shieldPulses.length - 1; i >= 0; i--) {
        const sp = shieldPulses[i]
        sp.radius += 1.5
        sp.opacity = 0.25 * (1 - sp.radius / sp.maxRadius) * gridOpacity

        if (sp.radius > sp.maxRadius || sp.opacity < 0.003) { shieldPulses.splice(i, 1); continue }

        ctx.beginPath()
        ctx.arc(sp.x, sp.y, sp.radius, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(165, 180, 252, ${sp.opacity})`  // indigo-300
        ctx.lineWidth = 1
        ctx.stroke()

        if (sp.radius > 20) {
          ctx.beginPath()
          ctx.arc(sp.x, sp.y, sp.radius * 0.6, 0, Math.PI * 2)
          ctx.strokeStyle = `rgba(255, 255, 255, ${sp.opacity * 0.2})`
          ctx.lineWidth = 0.4
          ctx.stroke()
        }
      }

      // ─ Vignette ─
      const vignette = ctx.createRadialGradient(
        width / 2, height * 0.4, height * 0.3,
        width / 2, height / 2, Math.max(width, height) * 0.85
      )
      vignette.addColorStop(0, 'transparent')
      vignette.addColorStop(0.5, 'rgba(5, 5, 18, 0.06)')
      vignette.addColorStop(0.8, 'rgba(5, 5, 18, 0.2)')
      vignette.addColorStop(1, 'rgba(5, 5, 18, 0.4)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, width, height)

      animFrame = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', resizeCanvas)
      canvas.removeEventListener('click', handleClick)
      cancelAnimationFrame(animFrame)
    }
  }, [handleClick])

  return (
    <canvas
      ref={canvasRef}
      className={`${absolute ? 'absolute' : 'fixed'} inset-0 ${className}`}
      style={{ opacity, zIndex, pointerEvents: 'auto' }}
      aria-hidden="true"
    />
  )
}
